/**
 * NoterPage - Guide about notes in annual reports
 *
 * SEO Target: /arsredovisning/guider/noter
 * Primary keywords: noter årsredovisning, tilläggsupplysningar, K2 noter
 */

import { Link } from "react-router-dom";
import { PageMeta, getBreadcrumbSchema, SEO_CONFIG } from "@/lib/seo";
import { Breadcrumbs } from "@/layouts/PublicLayout";
import { Helmet } from "react-helmet-async";

const CheckIcon = () => (
  <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

export function NoterPage() {
  const breadcrumbItems = [
    { label: "Årsredovisning", href: "/arsredovisning" },
    { label: "Guider", href: "/arsredovisning/guider" },
    { label: "Noter" },
  ];

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: "Hem", url: "/" },
    { name: "Årsredovisning", url: "/arsredovisning" },
    { name: "Guider", url: "/arsredovisning/guider" },
    { name: "Noter", url: "/arsredovisning/guider/noter" },
  ]);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Noter i Årsredovisningen - Komplett guide",
    description:
      "Lär dig allt om noter i årsredovisningen. Vilka noter som är obligatoriska, hur de ska utformas och skillnader mellan K2 och K3.",
    author: {
      "@type": "Organization",
      name: "Kvanten",
    },
    publisher: {
      "@type": "Organization",
      name: "Kvanten",
      url: SEO_CONFIG.siteUrl,
    },
    datePublished: "2025-01-20",
    dateModified: "2025-01-30",
  };

  return (
    <>
      <PageMeta
        title="Noter i Årsredovisningen | Vilka Noter Behövs? | Kvanten"
        description="Komplett guide om noter i årsredovisningen. Lär dig vilka noter som är obligatoriska enligt K2, hur de ska utformas och vad de ska innehålla."
        keywords="noter årsredovisning, tilläggsupplysningar, K2 noter, obligatoriska noter, redovisningsprinciper"
        canonicalUrl={`${SEO_CONFIG.siteUrl}/arsredovisning/guider/noter`}
        fullTitle
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-b from-teal-50 to-white py-12 lg:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={breadcrumbItems} />

          <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            <span>Guide</span>
            <span>•</span>
            <span>10 min läsning</span>
            <span>•</span>
            <span>Uppdaterad jan 2025</span>
          </div>

          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            Noter i årsredovisningen: Komplett guide
          </h1>

          <p className="text-xl text-gray-600">
            Noter (tilläggsupplysningar) är en viktig del av årsredovisningen som förklarar och
            kompletterar resultat- och balansräkningen. Den här guiden går igenom vilka noter som
            krävs och hur de ska utformas.
          </p>
        </div>
      </section>

      {/* Content */}
      <article className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Table of contents */}
          <div className="bg-gray-50 rounded-xl p-6 mb-12">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Innehåll</h2>
            <nav>
              <ol className="space-y-2 text-blue-600">
                <li>
                  <a href="#vad-ar-noter" className="hover:underline">
                    1. Vad är noter?
                  </a>
                </li>
                <li>
                  <a href="#obligatoriska" className="hover:underline">
                    2. Obligatoriska noter (K2)
                  </a>
                </li>
                <li>
                  <a href="#villkorade" className="hover:underline">
                    3. Villkorade noter
                  </a>
                </li>
                <li>
                  <a href="#k2-vs-k3" className="hover:underline">
                    4. Skillnader K2 vs K3
                  </a>
                </li>
                <li>
                  <a href="#tips" className="hover:underline">
                    5. Tips för bra noter
                  </a>
                </li>
              </ol>
            </nav>
          </div>

          {/* Section 1 */}
          <section id="vad-ar-noter" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Vad är noter?</h2>
            <p className="text-gray-600 mb-4">
              Noter (eller tilläggsupplysningar) är kompletterande information till resultat- och
              balansräkningen. De ger läsaren fördjupad förståelse för siffrorna i den finansiella
              rapporteringen.
            </p>
            <p className="text-gray-600 mb-4">Noterna fyller flera viktiga funktioner:</p>
            <ul className="space-y-2 mb-6">
              {[
                "Förklarar vilka redovisningsprinciper företaget tillämpar",
                "Ger detaljerad information om poster i balans- och resultaträkningen",
                "Upplysningar som krävs enligt lag men inte passar i huvudrapporterna",
                "Förklarar väsentliga bedömningar och antaganden",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckIcon />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>

            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="font-semibold text-blue-900 mb-2">Numrering av noter</h3>
              <p className="text-blue-800">
                Noter numreras löpande (Not 1, Not 2, osv.) och varje post i resultat- och
                balansräkningen som har en not hänvisas till med notnumret. Den första noten
                är alltid redovisningsprinciper.
              </p>
            </div>
          </section>

          {/* Section 2 */}
          <section id="obligatoriska" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Obligatoriska noter (K2)</h2>
            <p className="text-gray-600 mb-6">
              Enligt K2 (BFNAR 2016:10) är följande noter <strong>alltid obligatoriska</strong> för
              aktiebolag:
            </p>

            <div className="space-y-6">
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Not 1: Redovisningsprinciper</h3>
                <p className="text-gray-600 mb-3">Ska alltid innehålla:</p>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li>• Att årsredovisningen är upprättad enligt ÅRL och BFNAR 2016:10 (K2)</li>
                  <li>• Värderingsprinciper för tillgångar, skulder och avsättningar</li>
                  <li>• Intäktsredovisningsprinciper</li>
                  <li>• Eventuella avvikelser från tidigare år med motivering</li>
                </ul>
              </div>

              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="font-semibold text-gray-900 mb-3">
                  Not: Förändring av eget kapital
                </h3>
                <p className="text-gray-600 mb-3">
                  Obligatorisk för aktiebolag. Ska visa förändringen i:
                </p>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li>• Aktiekapital</li>
                  <li>• Överkursfond (om tillämpligt)</li>
                  <li>• Balanserat resultat (inkl. utdelning)</li>
                  <li>• Årets resultat</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section id="villkorade" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Villkorade noter</h2>
            <p className="text-gray-600 mb-6">
              Dessa noter är <strong>obligatoriska endast om</strong> företaget har relevanta poster
              i bokföringen:
            </p>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="text-left py-3 px-4 border font-semibold">Not</th>
                    <th className="text-left py-3 px-4 border font-semibold">Krävs när...</th>
                    <th className="text-left py-3 px-4 border font-semibold">Lagrum</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="py-3 px-4 border font-medium">Anläggningstillgångar</td>
                    <td className="py-3 px-4 border">
                      Det finns immateriella eller materiella anläggningstillgångar
                    </td>
                    <td className="py-3 px-4 border text-gray-500">ÅRL 5:8</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 border font-medium">Medelantal anställda</td>
                    <td className="py-3 px-4 border">
                      Det finns lönekostnader (konto 70xx, 72xx)
                    </td>
                    <td className="py-3 px-4 border text-gray-500">ÅRL 5:20</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 border font-medium">Ställda säkerheter</td>
                    <td className="py-3 px-4 border">
                      Det finns lån och säkerheter har ställts
                    </td>
                    <td className="py-3 px-4 border text-gray-500">ÅRL 5:14</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 border font-medium">Eventualförpliktelser</td>
                    <td className="py-3 px-4 border">
                      Det finns borgensåtaganden eller liknande
                    </td>
                    <td className="py-3 px-4 border text-gray-500">ÅRL 5:15</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 border font-medium">Obeskattade reserver</td>
                    <td className="py-3 px-4 border">
                      Det finns periodiseringsfonder eller överavskrivningar
                    </td>
                    <td className="py-3 px-4 border text-gray-500">ÅRL</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 border font-medium">Checkräkningskredit</td>
                    <td className="py-3 px-4 border">
                      Det finns beviljad checkräkningskredit
                    </td>
                    <td className="py-3 px-4 border text-gray-500">ÅRL</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 border font-medium">Skulder &gt;5 år</td>
                    <td className="py-3 px-4 border">
                      Det finns långfristiga skulder som förfaller efter 5 år
                    </td>
                    <td className="py-3 px-4 border text-gray-500">ÅRL 5:13</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 border font-medium">Goodwill</td>
                    <td className="py-3 px-4 border">
                      Det finns goodwill med avskrivningstid &gt;5 år
                    </td>
                    <td className="py-3 px-4 border text-gray-500">ÅRL 5:9</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 border font-medium">Exceptionella poster</td>
                    <td className="py-3 px-4 border">
                      Det finns intäkter/kostnader av exceptionell storlek
                    </td>
                    <td className="py-3 px-4 border text-gray-500">ÅRL 5:19</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 border font-medium">Moderföretag</td>
                    <td className="py-3 px-4 border">
                      Företaget är dotterbolag i en koncern
                    </td>
                    <td className="py-3 px-4 border text-gray-500">K2 19.2</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="bg-green-50 rounded-lg p-6 mt-6">
              <h3 className="font-semibold text-green-900 mb-2">
                Kvanten identifierar noter automatiskt
              </h3>
              <p className="text-green-800">
                När du laddar upp din SIE-fil till Kvanten analyserar vi automatiskt vilka konton
                som har saldon och triggar de noter som behövs. Du behöver aldrig fundera på vilka
                noter som krävs - vi tar hand om det.
              </p>
            </div>
          </section>

          {/* Section 4 */}
          <section id="k2-vs-k3" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Skillnader K2 vs K3</h2>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="text-left py-3 px-4 border font-semibold">Aspekt</th>
                    <th className="text-left py-3 px-4 border font-semibold bg-green-50">K2</th>
                    <th className="text-left py-3 px-4 border font-semibold bg-blue-50">K3</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="py-3 px-4 border font-medium">Antal noter</td>
                    <td className="py-3 px-4 border bg-green-50">~15 noter</td>
                    <td className="py-3 px-4 border bg-blue-50">~25-30 noter</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 border font-medium">Jämförelsetal i noter</td>
                    <td className="py-3 px-4 border bg-green-50">Frivilligt</td>
                    <td className="py-3 px-4 border bg-blue-50">Obligatoriskt</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 border font-medium">Könsfördelning personal</td>
                    <td className="py-3 px-4 border bg-green-50">Krävs ej</td>
                    <td className="py-3 px-4 border bg-blue-50">Krävs för större ftg</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 border font-medium">Uppskjuten skatt</td>
                    <td className="py-3 px-4 border bg-green-50">Redovisas ej</td>
                    <td className="py-3 px-4 border bg-blue-50">Egen not krävs</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 border font-medium">Leasingavtal</td>
                    <td className="py-3 px-4 border bg-green-50">Förenklad upplysning</td>
                    <td className="py-3 px-4 border bg-blue-50">Detaljerad not</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 border font-medium">Finansiella risker</td>
                    <td className="py-3 px-4 border bg-green-50">Krävs ej</td>
                    <td className="py-3 px-4 border bg-blue-50">Obligatorisk not</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 5 */}
          <section id="tips" className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Tips för bra noter</h2>

            <div className="space-y-4">
              {[
                {
                  title: "Var konsekvent",
                  description:
                    "Använd samma format och struktur för noter mellan åren. Det underlättar jämförelser.",
                },
                {
                  title: "Var tydlig, inte pratig",
                  description:
                    "Noter ska ge relevant information, inte upprepa det som redan framgår av siffrorna.",
                },
                {
                  title: "Hoppa över noll-noter",
                  description:
                    "Om en not skulle visa endast nollor (t.ex. inga anställda) behövs den inte alls i K2.",
                },
                {
                  title: "Avrunda korrekt",
                  description:
                    "Belopp i noter ska avrundas på samma sätt som i resultat- och balansräkning.",
                },
                {
                  title: "Kontrollera summeringar",
                  description:
                    "Siffror i noter ska stämma med motsvarande poster i balans-/resultaträkning.",
                },
              ].map((item, i) => (
                <div key={i} className="border border-gray-200 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">
              Låt Kvanten generera dina noter automatiskt
            </h2>
            <p className="text-teal-100 mb-6">
              Slipp manuellt arbete med noter. Kvanten analyserar din SIE-fil och skapar alla
              obligatoriska noter automatiskt - korrekt formaterade enligt K2 eller K3.
            </p>
            <Link
              to="/registrera"
              className="inline-flex items-center px-6 py-3 text-lg font-medium text-teal-600 bg-white rounded-lg hover:bg-teal-50 transition-colors"
            >
              Kom igång gratis
            </Link>
          </section>

          {/* Related articles */}
          <section className="mt-12">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Relaterade guider</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  title: "Förvaltningsberättelsen",
                  href: "/arsredovisning/guider/forvaltningsberattelse",
                  description: "Guide om förvaltningsberättelsens innehåll",
                },
                {
                  title: "K2 vs K3",
                  href: "/arsredovisning/guider/k2-vs-k3",
                  description: "Jämförelse av regelverken",
                },
              ].map((article, i) => (
                <Link
                  key={i}
                  to={article.href}
                  className="block p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <h3 className="font-semibold text-gray-900 mb-2">{article.title}</h3>
                  <p className="text-sm text-gray-600">{article.description}</p>
                </Link>
              ))}
            </div>
          </section>
        </div>
      </article>
    </>
  );
}
